import { useState, useEffect } from 'react';
import { Filter } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { usePricing, formatPrice } from '../context/PricingContext';
import SEO from '../components/SEO';
import SubjectCard from '../components/SubjectCard';
import CourseInfoBanner from '../components/CourseInfoBanner';
import { BreadcrumbSchema, CourseListSchema } from '../components/StructuredData';

// Logo URLs
const cambridgeLogo = "https://customer-assets.emergentagent.com/job_exam-results-hub-1/artifacts/ed6r44wi_cambridge-international-school-seeklogo.png";
const edexcelLogo = "/edexcel-logo.png";

const IGCSEPage = () => {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [examBoardFilter, setExamBoardFilter] = useState('all');
  const pricing = usePricing();

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const { data, error } = await supabase.from('subjects').select('*').eq('level', 'IGCSE');
        if (error) throw error;
        setSubjects(data);
      } catch (e) {
        console.error('Error fetching subjects:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchSubjects();
  }, []);

  const filteredSubjects = examBoardFilter === 'all' 
    ? subjects 
    : subjects.filter(s => s.exam_board.toLowerCase().includes(examBoardFilter.toLowerCase()));

  const isLocal = pricing.countryCode === 'LK';

  const getGroupPrice = (subject) => {
    return isLocal ? (subject.group_price_local || 2500) : (subject.group_price_international || 4000);
  };

  return (
    <main data-testid="igcse-page">
      <SEO
        title="IGCSE Programs"
        description="Browse all IGCSE (O Level) tuition programs at Aspire Academy. Cambridge & Edexcel syllabi, expert tutors, small batches. Mathematics, Sciences, Business, and more."
        path="/igcse"
      />
      <BreadcrumbSchema items={[{ name: 'Home', href: '/' }, { name: 'IGCSE Programs' }]} />
      <CourseListSchema courses={subjects} level="IGCSE" />
      {/* Hero */}
      <section className="bg-surface py-16 md:py-24">
        <div className="container-main">
          <div className="max-w-3xl">
            <span className="label-uppercase text-aspire-red">O Levels</span>
            <h1 className="heading-hero mt-2 mb-6">
              IGCSE <span className="text-aspire-red">Programs</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed font-medium">
              Comprehensive International GCSE programs following Cambridge Assessment International Education 
              and Edexcel syllabi. Our courses are designed to build strong academic foundations 
              and prepare students for advanced studies.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 text-base font-bold">
              First Demo Class FREE - Try before you commit!
            </div>
          </div>
        </div>
      </section>

      {/* Filter & Subjects Grid */}
      <section className="section-spacing bg-white">
        <div className="container-main">
          {/* Filter */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 sm:mb-12">
            <div>
              <h2 className="heading-section">Available Subjects</h2>
              <p className="text-gray-600 mt-2">Select a subject to view details and enroll</p>
            </div>
            <div className="flex items-center gap-4">
              <Filter className="w-5 h-5 text-gray-400" />
              <Select value={examBoardFilter} onValueChange={setExamBoardFilter}>
                <SelectTrigger className="w-[180px]" data-testid="exam-board-filter">
                  <SelectValue placeholder="Exam Board" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Exam Boards</SelectItem>
                  <SelectItem value="cambridge">Cambridge</SelectItem>
                  <SelectItem value="edexcel">Edexcel</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Subjects Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aspire-red mx-auto"></div>
            </div>
          ) : (
            <>
              <CourseInfoBanner
                price={filteredSubjects[0] ? getGroupPrice(filteredSubjects[0]) : (isLocal ? 2500 : 4000)}
                duration="2 hours"
                maxStudents={8}
                targetGrade="A* - B"
                context={
                  examBoardFilter === 'all'
                    ? 'All IGCSE subjects · Cambridge & Edexcel syllabi'
                    : `${examBoardFilter.charAt(0).toUpperCase() + examBoardFilter.slice(1)} IGCSE subjects`
                }
              />
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {filteredSubjects.map((subject) => (
                  <SubjectCard
                    key={subject.subject_id}
                    subject={subject}
                    to={`/igcse/${subject.slug}`}
                    showExamBoard
                  />
                ))}
              </div>
            </>
          )}

          {!loading && filteredSubjects.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No subjects found matching your filter.</p>
            </div>
          )}
        </div>
      </section>

      {/* Pricing Info */}
      <section className="section-spacing bg-charcoal">
        <div className="container-main">
          <div className="text-center mb-12">
            <span className="label-uppercase text-amber-500">Class Options</span>
            <h2 className="heading-section mt-2 text-white">Flexible Learning Options</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/5 backdrop-blur p-4 sm:p-8">
              <h3 className="font-heading font-bold text-xl text-white mb-4">Group Classes</h3>
              <div className="font-mono text-3xl text-aspire-red mb-4">
                {formatPrice(isLocal ? 2500 : 4000, pricing)}
                <span className="text-sm text-gray-400 font-sans block">per 2-hour session</span>
              </div>
              <ul className="space-y-3 text-gray-300 text-sm">
                <li>• Maximum 7-8 students per batch</li>
                <li>• Interactive group discussions</li>
                <li>• Peer learning environment</li>
                <li>• First session FREE</li>
              </ul>
            </div>
            <div className="bg-white/5 backdrop-blur p-4 sm:p-8">
              <h3 className="font-heading font-bold text-xl text-white mb-4">Individual Classes</h3>
              <div className="font-mono text-3xl text-aspire-red mb-4">
                {isLocal ? (
                  <>{formatPrice(3500, pricing)} - {formatPrice(4000, pricing)}</>
                ) : (
                  formatPrice(6000, pricing)
                )}
                <span className="text-sm text-gray-400 font-sans block">per 2-hour session</span>
              </div>
              <ul className="space-y-3 text-gray-300 text-sm">
                <li>• One-on-one attention</li>
                <li>• Personalized learning pace</li>
                <li>• {isLocal ? 'Online or physical options' : 'Online sessions'}</li>
                <li>• First session FREE</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Exam Boards Info */}
      <section className="section-spacing bg-surface">
        <div className="container-main">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-4 sm:p-8 border-l-4 border-aspire-red">
              <img src={cambridgeLogo} alt="Cambridge" className="h-12 w-auto mb-6" />
              <h3 className="font-heading font-bold text-2xl mb-4">Cambridge IGCSE</h3>
              <p className="text-gray-600 text-base leading-relaxed mb-4 font-medium">
                Cambridge IGCSE is the world's most popular international qualification for 14 to 16 year olds. 
                Recognized by leading universities and employers worldwide.
              </p>
              <ul className="text-base text-gray-600 space-y-2 font-medium">
                <li>• Examination sessions: May/June and October/November</li>
                <li>• Grade scale: A* to G</li>
                <li>• Internationally recognized qualification</li>
              </ul>
            </div>
            <div className="bg-white p-4 sm:p-8 border-l-4 border-charcoal">
              <img src={edexcelLogo} alt="Edexcel" className="h-12 w-auto mb-6 bg-white px-2 py-1" />
              <h3 className="font-heading font-bold text-2xl mb-4">Edexcel IGCSE</h3>
              <p className="text-gray-600 text-base leading-relaxed mb-4 font-medium">
                Pearson Edexcel International GCSE provides an academically rigorous, 
                internationally relevant qualification that develops students' skills in creative thinking, 
                enquiry and problem solving.
              </p>
              <ul className="text-base text-gray-600 space-y-2 font-medium">
                <li>• Examination sessions: January and May/June</li>
                <li>• Grade scale: 9 to 1</li>
                <li>• UK university recognized</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default IGCSEPage;
